import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { basicHead } from '../../../utility/head';
import { BottomCTA } from '../../../components/BottomCTA';
import {
  findBlogCategoryBySlug,
  findBlogPostBySlug,
  type BlogCategoryInterface,
} from '../../../assets/data/blogs';
import NotFound from '../../../components/NotFound';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { ContentfulText } from '../../../components/ContentfulText';
import {
  ContentfulImage,
  contentfulOgImageUrl,
} from '../../../components/ContentfulImage';

export const Route = createFileRoute('/_marketing/blog/$slug')({
  component: RouteComponent,
  ssr: true,
  loader: async ({ params, location }) => {
    // Categories share this route with posts; category slugs win.
    const category = findBlogCategoryBySlug(params.slug);

    if (category) {
      return {
        pathname: location.pathname,
        category,
        post: undefined,
        seo: {
          title: `${category.name} - Stark Crawl Blog`,
          // The explainer is written for this category, so it beats a template.
          description:
            category.explainer ??
            `All Stark Crawl blog posts about ${category.name.toLowerCase()} — practical guides on AI knowledge bases and knowledge management.`,
          keywords: `${category.name}, AI knowledge base blog, Stark Crawl blog`,
        },
      };
    }

    const post = await findBlogPostBySlug(params.slug);

    return {
      pathname: location.pathname,
      category: undefined,
      post,
      seo: {
        title: post ? `${post.title} - Stark Crawl` : 'Not Found - Stark Crawl',
        description: post?.excerpt,
        type: post ? ('article' as const) : undefined,
        keywords: post
          ? `${post.category}, AI knowledge base, Stark Crawl`
          : undefined,
        image: post?.heroImage
          ? { url: contentfulOgImageUrl(post.heroImage) }
          : undefined,
        schema: post
          ? {
              '@context': 'https://schema.org',
              '@type': 'BlogPosting',
              headline: post.title,
              description: post.excerpt,
              image: post.heroImage,
              datePublished: post.publishedAt,
              publisher: { '@type': 'Organization', name: 'Stark Crawl' },
            }
          : undefined,
      },
    };
  },
  head: (ctx) => {
    const base = basicHead(ctx);

    return {
      ...base,
      links: [
        ...('links' in base ? base.links : []),
        { rel: 'preconnect', href: 'https://images.ctfassets.net' },
      ],
    };
  },
});

function CategoryPage({ category }: { category: BlogCategoryInterface }) {
  return (
    <div className="overflow-x-hidden">
      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <section className="pt-15 lg:pt-20 pb-10 px-5">
        <div className="max-w-200 mx-auto text-center">
          <Breadcrumbs
            center
            items={[
              { label: 'Home', path: '/' },
              { label: 'Blog', path: '/blog' },
              { label: category.name },
            ]}
          />
          <h1 className="font-grotesque text-[2.625rem] leading-[1.15] lg:text-[4.375rem] lg:leading-none font-medium text-white mt-5">
            {category.name}
          </h1>
          {category.explainer && (
            <p className="font-open text-base leading-7 lg:text-lg lg:leading-8 text-grey-200 mt-5 text-balance">
              {category.explainer}
            </p>
          )}
        </div>
      </section>

      {/* ─── Posts ───────────────────────────────────────────────────────── */}
      <section className="pb-25 px-5">
        <ul className="max-w-200 mx-auto flex flex-col" role="list">
          {category.posts.map((post) => (
            <li
              key={post.slug}
              className="border-b border-grey-t-800 last:border-b-0"
            >
              <Link
                to="/blog/$slug"
                params={{ slug: post.slug }}
                className="flex flex-col gap-1 rounded-xs p-5 hover:bg-grey-t-900 transition-colors group"
              >
                <h2 className="font-grotesque text-[1.875rem] leading-9 font-medium text-white">
                  {post.title}
                </h2>
                <p className="font-open text-base leading-6 text-grey-200">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-end pt-2 w-full">
                  <ArrowRightIcon
                    className="w-6 h-6 text-grey-400 group-hover:text-white transition-colors"
                    aria-hidden="true"
                  />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* ─── Bottom CTA ──────────────────────────────────────────────────── */}
      <BottomCTA />
    </div>
  );
}

function RouteComponent() {
  const { category, post } = Route.useLoaderData();

  if (category) return <CategoryPage category={category} />;

  if (!post) return <NotFound to="/blog" />;

  return (
    <article className="overflow-x-hidden">
      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <section className="pt-15 lg:pt-20 pb-10 px-5">
        <div className="max-w-200 mx-auto text-center">
          <Breadcrumbs
            center
            items={[
              { label: 'Home', path: '/' },
              { label: 'Blog', path: '/blog' },
              { label: post.category },
            ]}
          />
          <h1 className="font-grotesque text-[2.25rem] leading-[1.15] lg:text-[3.5rem] lg:leading-[1.1] font-medium text-white mb-5">
            {post.title}
          </h1>
          <p className="text-lg leading-7 text-grey-300 max-w-160 mx-auto">
            {post.excerpt}
          </p>
        </div>
      </section>

      {/* ─── Hero Image ──────────────────────────────────────────────────── */}
      {post.heroImage && (
        <section className="pb-15 px-5">
          <div className="max-w-315 mx-auto">
            <ContentfulImage
              src={post.heroImage}
              alt={post.title}
              className="w-full max-w-250 mx-auto rounded-xs object-cover max-h-120"
              sizes="(max-width: 1040px) 100vw, 1000px"
              fetchPriority="high"
            />
          </div>
        </section>
      )}

      {/* ─── Content ─────────────────────────────────────────────────────── */}
      <section className="pb-20 px-5">
        <div className="max-w-200 mx-auto">
          {post.body && (
            <ContentfulText
              document={post.body}
              entries={post.entries}
              assets={post.assets}
            />
          )}
        </div>
      </section>

      {/* ─── Bottom CTA ──────────────────────────────────────────────────── */}
      <BottomCTA />
    </article>
  );
}
